/**
 * FieldRenderer — renders one custom form field on the public pre-order form
 * (and in the builder preview). Reads the field's `type` from the shared
 * registry; the same component handles every field type so adding a type only
 * touches fieldTypes.js + this switch.
 *
 * `onBlur` (optional) drives inline on-blur validation; per-type inputMode /
 * autoComplete / maxLength improve mobile + a11y. value shape: array for
 * multi_choice, scalar for everything else.
 */
import React from 'react';
import { fieldIsInput } from '../fieldTypes';
import PhoneField from './PhoneField';

// Per-type input attributes (mobile keyboards, autofill, length caps).
const TYPE_ATTRS = {
  short_text: { autoComplete: 'off', maxLength: 200 },
  long_text:  { maxLength: 1000 },
  number:     { inputMode: 'decimal', maxLength: 20 },
  email:      { inputMode: 'email', autoComplete: 'email', maxLength: 120 },
  phone:      { inputMode: 'tel', autoComplete: 'tel', maxLength: 25 },
};

export default function FieldRenderer({ field, value, onChange, onBlur, error }) {
  const { id, type, label, required, placeholder, helpText, options = [] } = field;

  // Presentational block (section heading) — no input, no value.
  if (!fieldIsInput(type)) {
    return (
      <div className="evf-heading">
        <h3>{label || 'Section'}</h3>
        {helpText ? <p>{helpText}</p> : null}
      </div>
    );
  }

  const set = (v) => onChange(v);
  const attrs = TYPE_ATTRS[type] || {};
  const invalid = error ? true : undefined;
  let control;

  switch (type) {
    case 'long_text':
      control = (
        <>
          <textarea className="evf-input" rows={3} placeholder={placeholder} aria-invalid={invalid} {...attrs}
            value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />
          <span className="evf-count">{String(value || '').length}/{attrs.maxLength}</span>
        </>
      );
      break;
    case 'number':
      control = (
        <input className="evf-input" type="number" placeholder={placeholder} aria-invalid={invalid} {...attrs}
          value={value ?? ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />
      );
      break;
    case 'date':
      control = <input className="evf-input" type="date" aria-invalid={invalid} value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />;
      break;
    case 'time':
      control = <input className="evf-input" type="time" aria-invalid={invalid} value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />;
      break;
    case 'email':
      control = (
        <input className="evf-input" type="email" placeholder={placeholder || 'you@example.com'} aria-invalid={invalid} {...attrs}
          value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />
      );
      break;
    case 'phone':
      control = <PhoneField value={value || ''} error={!!error} onChange={set} onBlur={onBlur} />;
      break;
    case 'dropdown':
      control = (
        <select className="evf-input" aria-invalid={invalid} value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur}>
          <option value="">Select…</option>
          {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
      );
      break;
    case 'single_choice':
      control = (
        <div className="evf-choices">
          {options.map((o, i) => (
            <label key={i} className={`evf-choice${value === o ? ' is-on' : ''}`}>
              <input type="radio" name={id} checked={value === o} onChange={() => set(o)} onBlur={onBlur} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
      break;
    case 'multi_choice': {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (o) => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      control = (
        <div className="evf-choices">
          {options.map((o, i) => (
            <label key={i} className={`evf-choice${arr.includes(o) ? ' is-on' : ''}`}>
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} onBlur={onBlur} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
      break;
    }
    default:
      control = (
        <input className="evf-input" type="text" placeholder={placeholder} aria-invalid={invalid} {...attrs}
          value={value || ''} onChange={(e) => set(e.target.value)} onBlur={onBlur} />
      );
  }

  return (
    <div className={`evf-field${error ? ' has-error' : ''}`}>
      <label className="evf-label">
        {label || 'Question'}{required ? <span className="evf-req">*</span> : null}
      </label>
      {helpText ? <p className="evf-help">{helpText}</p> : null}
      {control}
      {error ? <span className="evf-err" role="alert">{error}</span> : null}
    </div>
  );
}
